import { createContext, useContext, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ApiClient } from "@carepass/api-client";
import { useStore } from "zustand";
import type { AdminSessionLifecycle, AdminSessionSnapshot } from "./session-lifecycle";
import type { AdminUiState, AdminUiStore } from "./stores/admin-ui-store";
import type { QueryClient } from "@tanstack/react-query";
import type { AsyncTaskRegistry, PollingPolicy } from "@carepass/async-task";

export interface AdminApplicationServices {
  readonly apiClient: ApiClient;
  readonly queryClient: QueryClient;
  readonly uiStore: AdminUiStore;
  readonly sessionLifecycle: AdminSessionLifecycle;
  readonly asyncTasks: AsyncTaskRegistry;
  readonly polling: PollingPolicy;
}

const AdminApplicationContext = createContext<AdminApplicationServices | null>(null);

export function AdminAppProviders({
  children,
  services,
}: {
  readonly children: ReactNode;
  readonly services: AdminApplicationServices;
}) {
  return (
    <AdminApplicationContext.Provider value={services}>
      <QueryClientProvider client={services.queryClient}>{children}</QueryClientProvider>
    </AdminApplicationContext.Provider>
  );
}

export function useAdminApplication(): AdminApplicationServices {
  const services = useContext(AdminApplicationContext);
  if (!services) throw new Error("AdminAppProviders is required.");
  return services;
}

export function useAdminSession(): AdminSessionSnapshot {
  const { sessionLifecycle } = useAdminApplication();
  return useSyncExternalStore(
    sessionLifecycle.subscribe,
    sessionLifecycle.getSnapshot,
    sessionLifecycle.getSnapshot,
  );
}

export function useAdminUiStore<T>(selector: (state: AdminUiState) => T): T {
  const { uiStore } = useAdminApplication();
  return useStore(uiStore, selector);
}
